import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, User, CheckCircle, AlertCircle, TrendingUp, FileText, HelpCircle, Download, Percent, History } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import followupAttachmentService from '../../../../../../services/audit_services/followupAttachmentService';

export default function AdminRecommendationActionDetails() {
  const { actionId } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);

  useEffect(() => {
    fetchActionDetails();
  }, [actionId]);

  const fetchActionDetails = async () => {
    setLoading(true);
    
    const result = await adminAuditService.getRecommendationActionDetail(authTokens.access, actionId);
    
    if (result.success) {
      setAction(result.data);
    } else {
      showError(result.error || 'Erreur lors du chargement des détails de l\'action');
    }
    
    setLoading(false);
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachment(attachmentId);
    
    const result = await followupAttachmentService.downloadAttachment(authTokens.access, attachmentId, fileName);
    
    if (result.success) {
      showSuccess('Téléchargement réussi');
    } else {
      showError(result.error || 'Erreur lors du téléchargement');
    }
    
    setDownloadingAttachment(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complété' },
      achieved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' }
    };

    const config = priorityConfig[priority] || priorityConfig.medium;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de l'action...</p>
        </div>
      </div>
    );
  }

  if (!action) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Action de recommandation non trouvée</p>
        <Link to="/dashboard/admin/audits/recommendation-actions" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux actions de recommandation
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/admin/audits/recommendation-actions"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Action de recommandation</h1>
            <p className="text-sm text-gray-600 mt-1">Détails de l'action de recommandation</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/dashboard/admin/audits/recommendation-actions/${actionId}/history`}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            <History className="h-4 w-4 mr-2" />
            Historique
          </Link>
          {getStatusBadge(action.status)}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Recommendation Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de la recommandation</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Texte de la recommandation</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.recommendation_text}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Éléments de la recommandation</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.elements_of_recommendation || 'Non défini'}</p>
              </div>

              {action.kpi_of_recommendation && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2 text-green-600" />
                    KPI de la recommandation
                  </p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{action.kpi_of_recommendation}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Propriétaire</p>
                <p className="text-sm text-gray-900">{action.recommendation_owner_name}</p>
              </div>

              {action.delay_of_recommendation && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-green-600" />
                    Échéance
                  </p>
                  <p className="text-sm text-gray-900">
                    {new Date(action.delay_of_recommendation).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Question Recommendation Info */}
          {action.question_recommendation_info && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                Recommandation de question
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Recommandation</p>
                  <p className="text-sm text-gray-900 mt-1">{action.question_recommendation_info.recommendation}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Priorité</p>
                  <div className="mt-1">{getPriorityBadge(action.question_recommendation_info.priority)}</div>
                </div>
                {action.question_recommendation_info.audit_question && (
                  <>
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <HelpCircle className="h-4 w-4 mr-2 text-blue-600" />
                        Question d'audit
                      </p>
                      <p className="text-sm text-gray-900">{action.question_recommendation_info.audit_question.question_name}</p>
                      {action.question_recommendation_info.audit_question.location && (
                        <p className="text-xs text-gray-600 mt-1">
                          Localisation: {action.question_recommendation_info.audit_question.location}
                        </p>
                      )}
                      {action.question_recommendation_info.audit_question.section_name && (
                        <p className="text-xs text-gray-600 mt-1">
                          Section: {action.question_recommendation_info.audit_question.section_name}
                        </p>
                      )}
                    </div>
                    <div className="pt-2">
                      <Link
                        to={`/dashboard/admin/audits/questions/${action.question_recommendation_info.audit_question.id}`}
                        className="text-sm text-green-600 hover:text-green-700 hover:underline"
                      >
                        Voir les détails de la question →
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Workflow Dates */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-green-600" />
              Dates du workflow
            </h3>
            <div className="space-y-3">
              {action.started_at && (
                <div>
                  <p className="text-xs text-gray-600">Date de démarrage</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(action.started_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              {action.completed_at && (
                <div>
                  <p className="text-xs text-gray-600">Date de complétion</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(action.completed_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              {action.achieved_date && (
                <div>
                  <p className="text-xs text-gray-600">Date d'archivage</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(action.achieved_date).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
              {!action.started_at && !action.completed_at && !action.achieved_date && (
                <p className="text-sm text-gray-500">Aucune date de workflow enregistrée</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Métadonnées</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1 flex items-center">
                  <User className="h-4 w-4 mr-1 text-gray-500" />
                  {action.created_by_name || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(action.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(action.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              {action.last_updated_by_name && (
                <div>
                  <p className="text-xs text-gray-600">Dernière modification par</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <User className="h-4 w-4 mr-1 text-gray-500" />
                    {action.last_updated_by_name}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Followups */}
          {action.follow_ups && action.follow_ups.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
                Suivis ({action.follow_ups.length})
              </h3>
              
              <div className="space-y-4">
                {action.follow_ups.map((followup) => (
                  <div key={followup.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    {/* Progression */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center">
                          <Percent className="h-4 w-4 mr-1 text-blue-600" />
                          Progression
                        </span>
                        <span className="text-sm font-semibold text-blue-700">{followup.progression_percentage}%</span>
                      </div>
                      {followup.progression_percentage > 100? (
                        <p></p>
                      ):(
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${followup.progression_percentage}%` }}
                          ></div>
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    {followup.status && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600 mb-1">Statut</p>
                        {getStatusBadge(followup.status)}
                      </div>
                    )}

                    {/* Notes */}
                    {followup.notes && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600 mb-1">Notes</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{followup.notes}</p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-3 pt-3 border-t border-blue-300 flex items-center justify-between text-xs text-gray-600">
                      <span className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        Par: {followup.created_by_name}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(followup.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>

                    {/* Attachments */}
                    {followup.attachments && followup.attachments.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-blue-300">
                        <p className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                          <FileText className="h-3 w-3 mr-1" />
                          Pièces jointes ({followup.attachments.length})
                        </p>
                        <div className="space-y-2">
                          {followup.attachments.map((attachment) => (
                            <div key={attachment.id} className="p-3 bg-white border border-blue-200 rounded flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{attachment.file_name}</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                  Taille: {formatFileSize(attachment.file_size)}
                                </p>
                                {attachment.description && (
                                  <p className="text-xs text-gray-700 mt-1">{attachment.description}</p>
                                )}
                                <div className="mt-1 flex items-center text-xs text-gray-500">
                                  <span>Téléchargé par: {attachment.uploaded_by_name}</span>
                                  <span className="mx-2">•</span>
                                  <span>{new Date(attachment.created_at).toLocaleDateString('fr-FR')}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                                disabled={downloadingAttachment === attachment.id}
                                className="ml-3 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
                              >
                                {downloadingAttachment === attachment.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                    Téléchargement...
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-3 w-3 mr-1" />
                                    Télécharger
                                  </>
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
